import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Platform } from 'react-native';
import { WebView } from 'react-native-webview';
import type { WebViewErrorEvent } from 'react-native-webview/lib/WebViewTypes';

import { theme } from '@squire/ui';

import type { BottomSheetRef } from '@/components/_shared/BottomSheet';
import { BottomSheet } from '@/components/_shared/BottomSheet';
import { toast } from '@/components/_shared/Toaster';
import { config } from '@/constants/config';
import { useScreenTranslations } from '@/hooks/useScreenTranslations';

import { ScreenHeader } from '../ScreenHeader';
import { styles, webIFrameStyle } from './styles';
import type { LegalBottomSheetProps, LegalDocumentType } from './types';

export interface LegalBottomSheetRef {
  open: (type: LegalDocumentType) => void;
  close: () => void;
}

const LegalBottomSheet = forwardRef<LegalBottomSheetRef, LegalBottomSheetProps>(
  ({ onDismiss, testID = 'legal-bottom-sheet' }, ref) => {
    const { t } = useScreenTranslations('LegalBottomSheet');
    const bottomSheetRef = useRef<BottomSheetRef>(null);

    const [documentType, setDocumentType] = useState<LegalDocumentType | null>(
      null,
    );
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    const url = documentType === 'terms' ? config.termsUrl : config.privacyUrl;
    const title =
      documentType === 'terms' ? t('termsTitle') : t('privacyTitle');

    useEffect(() => {
      if (error && !loading) {
        toast({
          variant: 'error',
          message: t('errorTitle'),
          description: t('errorMessage'),
        });

        bottomSheetRef.current?.close();

        setError(false);
      }
    }, [error, loading, t]);

    const handleLoadEnd = () => {
      setLoading(false);
    };

    const handleError = (_event: WebViewErrorEvent) => {
      setLoading(false);
      setError(true);
    };

    const handleClose = () => {
      bottomSheetRef.current?.close();
    };

    const handleDismiss = () => {
      setLoading(true);
      setError(false);
      setDocumentType(null);
      onDismiss?.();
    };

    useImperativeHandle(ref, () => ({
      open: (type: LegalDocumentType) => {
        setDocumentType(type);
        setLoading(true);
        setError(false);
        bottomSheetRef.current?.open();
      },
      close: () => {
        bottomSheetRef.current?.close();
      },
    }));

    return (
      <BottomSheet
        ref={bottomSheetRef}
        testID={testID}
        scrollable={false}
        hideHandle
        onDismiss={handleDismiss}
        snapPoints={[{ percentage: loading ? '20%' : '85%' }]}
        style={styles.bottomSheet}
      >
        <ScreenHeader
          title={title}
          onClose={handleClose}
          isLoading={loading}
          paddingHorizontal={theme.spacing[5]}
          paddingVertical={theme.spacing[4]}
        />

        {/* WebView/iframe content */}
        {!error && documentType && (
          <>
            {Platform.OS === 'web' ? (
              <iframe
                src={url}
                style={webIFrameStyle}
                title={title}
                onLoad={handleLoadEnd}
                onError={() => setError(true)}
              />
            ) : (
              <WebView
                source={{ uri: url }}
                onLoadEnd={handleLoadEnd}
                originWhitelist={['http://', 'https://', 'about:']}
                onError={handleError}
                javaScriptEnabled
                domStorageEnabled
                allowsInlineMediaPlayback
              />
            )}
          </>
        )}
      </BottomSheet>
    );
  },
);

LegalBottomSheet.displayName = 'LegalBottomSheet';
export { LegalBottomSheet };
